import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Package, Truck, Home } from 'lucide-react';
import AnnouncementBar from '../components/AnnouncementBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`).then(r => setOrder(r.data)).catch(() => setOrder({ error: true }));
  }, [orderId]);

  if (!order) {
    return (<><AnnouncementBar /><Header /><div className="py-24 text-center">Loading order...</div><Footer /></>);
  }
  if (order.error) {
    return (<><AnnouncementBar /><Header /><div className="py-24 text-center">Order not found.</div><Footer /></>);
  }

  return (
    <>
      <AnnouncementBar />
      <Header />
      <main className="max-w-[900px] mx-auto px-4 md:px-8 py-12">
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(63,82,37,0.12)' }}>
            <CheckCircle2 size={48} style={{ color: 'var(--gk-green)' }} />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold" style={{ color: 'var(--gk-green-dark)' }}>Dhanyavaad! Order placed</h1>
          <p className="mt-2 text-base" style={{ color: 'var(--gk-muted)' }}>Your fresh gaon spices are being packed with care.</p>
          <div className="mt-4 inline-block px-4 py-2 rounded-full" style={{ background: 'var(--gk-cream-dark)', color: 'var(--gk-green-dark)' }}>
            Order ID: <strong>{order.order_id}</strong>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {[
            { icon: CheckCircle2, label: 'Order Placed', active: true, sub: 'Confirmed just now' },
            { icon: Package, label: 'Packing', active: false, sub: 'Small-batch milling' },
            { icon: Truck, label: 'On the way', active: false, sub: 'Ships in 2 days' },
          ].map((s, i) => (
            <div key={i} className="p-4 rounded-xl text-center" style={{ background: s.active ? 'var(--gk-green)' : '#fff', color: s.active ? '#fff' : 'var(--gk-ink)', border: '1px solid #ecdfc7' }}>
              <s.icon size={22} className="mx-auto mb-2" style={{ color: s.active ? 'var(--gk-gold)' : 'var(--gk-green)' }} />
              <div className="font-semibold text-sm">{s.label}</div>
              <div className="text-xs opacity-80">{s.sub}</div>
            </div>
          ))}
        </div>

        <section className="p-6 rounded-xl mb-6" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
          <h2 className="font-serif text-xl font-bold mb-4" style={{ color: 'var(--gk-green-dark)' }}>Items ({order.items.length})</h2>
          <div className="space-y-3">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex gap-3 items-center">
                <img src={i.image} alt={i.name} className="w-14 h-14 rounded object-cover" />
                <div className="flex-1 text-sm">
                  <div className="font-semibold" style={{ color: 'var(--gk-ink)' }}>{i.name}</div>
                  <div className="text-xs" style={{ color: 'var(--gk-muted)' }}>{i.size} · Qty {i.qty}</div>
                </div>
                <div className="font-semibold" style={{ color: 'var(--gk-green-dark)' }}>₹{i.qty * i.price}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t space-y-1 text-sm" style={{ borderColor: '#ecdfc7' }}>
            <div className="flex justify-between"><span style={{ color: 'var(--gk-muted)' }}>Subtotal</span><span>₹{order.subtotal}</span></div>
            <div className="flex justify-between"><span style={{ color: 'var(--gk-muted)' }}>Shipping</span><span>{order.shipping === 0 ? 'Free' : `₹${order.shipping}`}</span></div>
            <div className="flex justify-between font-bold pt-2"><span style={{ color: 'var(--gk-green-dark)' }}>Total</span><span style={{ color: 'var(--gk-green-dark)' }}>₹{order.total}</span></div>
            <div className="flex justify-between text-xs pt-2" style={{ color: 'var(--gk-muted)' }}>
              <span>Payment</span><span>Cash on Delivery</span>
            </div>
          </div>
        </section>

        <section className="p-6 rounded-xl mb-8" style={{ background: '#fff', border: '1px solid #ecdfc7' }}>
          <h2 className="font-serif text-xl font-bold mb-3" style={{ color: 'var(--gk-green-dark)' }}>Shipping to</h2>
          <div className="text-sm" style={{ color: 'var(--gk-ink)' }}>
            <div className="font-semibold">{order.address.full_name}</div>
            <div style={{ color: 'var(--gk-muted)' }}>{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ''}</div>
            <div style={{ color: 'var(--gk-muted)' }}>{order.address.city}, {order.address.state} - {order.address.pincode}</div>
            <div className="mt-2" style={{ color: 'var(--gk-muted)' }}>Ph: {order.address.phone}</div>
          </div>
        </section>

        <div className="text-center">
          <button onClick={() => navigate('/')} className="gk-btn-primary"><Home size={16} /> Continue Shopping</button>
        </div>
      </main>
      <Footer />
    </>
  );
}
